import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { CircleDashed, TrendingUp, TrendingDown, BarChart2, Smartphone, Globe } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { useOutstandingSummary } from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface KeyMetricsScorecardProps {
  timeRange: "day" | "week" | "month" | "quarter" | "year";
  userRole: "manager" | "executive" | "callcenter" | "recovery";
}

interface MetricProps {
  title: string;
  value: string | number;
  target: string | number;
  status: "success" | "warning" | "danger" | "neutral";
  trend: "up" | "down" | "neutral";
  trendValue: number;
}

function Metric({ title, value, target, status, trend, trendValue }: MetricProps) {
  const statusColors = {
    success: "bg-green-50 text-green-800 border-green-100",
    warning: "bg-amber-50 text-amber-800 border-amber-100",
    danger: "bg-red-50 text-red-800 border-red-100",
    neutral: "bg-gray-50 text-gray-800 border-gray-100"
  };

  const trendColors = {
    up: "text-green-600",
    down: "text-red-600",
    neutral: "text-gray-600"
  };

  return (
    <div className={`p-2 rounded border`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-between items-center">
              <span className="card-subheading-text">{title}</span>
              <CircleDashed className="h-3 w-3 cursor-help" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Target: {target}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <div className="flex items-baseline justify-between mt-1">
        <span className="card-value-text">{value}</span>
        <span className={`text-xs flex items-center ${trendColors[trend]}`}>
          {trend === "up" ? <TrendingUp className="h-3 w-3 mr-0.5" /> : 
           trend === "down" ? <TrendingDown className="h-3 w-3 mr-0.5" /> : null}
          {trendValue}%
        </span>
      </div>
    </div>
  );
}

export function KeyMetricsScorecard({ timeRange, userRole }: KeyMetricsScorecardProps) {
     const remoteUtilityId = getRemoteUtilityId();
  const { 
    data: outstandingData, 
    isLoading: isOutstandingLoading, 
    error: outstandingError 
  } = useOutstandingSummary({
    remote_utility_id: remoteUtilityId,
  });

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000)}K`;
    } else {
      return `$${amount}`;
    }
  };

  // Helper function to determine status based on percentage
  const getCollectionStatus = (percentage: number, target: number) => {
    if (percentage >= target) return "success";
    if (percentage >= target * 0.9) return "warning";
    return "danger";
  };

  // Helper function to determine outstanding status
  const getOutstandingStatus = (amount: number, target: number) => {
    if (amount <= target) return "success";
    if (amount <= target * 1.2) return "warning";
    return "danger";
  };

  // Transform API data to financial metrics
  const financialMetrics = outstandingData?.result ? [
    {
      title: "Revenue Collection",
      value: `${outstandingData.result.paidPercentage}%`,
      target: "",
      status: getCollectionStatus(outstandingData.result.paidPercentage, 95),
      trend: outstandingData.result.paidPercentage >= 50 ? "up" as const : "down" as const,
      trendValue:outstandingData.result.paidPercentage // Assuming 50% as baseline
    },
    {
      title: "Outstanding Balance",
      value: outstandingData.result.totalOutstandingBalance,
      target: "",
      status: getOutstandingStatus(outstandingData.result.totalOutstandingBalance, 150000),
      trend: outstandingData.result.totalOutstandingBalance <= 150000 ? "up" as const : "down" as const,
      trendValue:outstandingData.result.totalOutstandingBalance
    }
  ] : [
    // Fallback data if API is not available
    {
      title: "Revenue Collection",
      value: "0%",
      target: "0%",
      status: "neutral" as const,
      trend: "neutral" as const,
      trendValue: 0
    },
    {
      title: "Outstanding Balance",
      value: "$0",
      target: "0K",
      status: "neutral" as const,
      trend: "neutral" as const,
      trendValue: 0
    }
  ];

  const digitalAdoptionMetrics = [
    {
      title: "Mobile App Usage",
      value: "0%",
      target: "75%",
      status: "warning" as const,
      trend: "up" as const,
      trendValue: 0,
      icon: <Smartphone className="h-3 w-3 ml-1" />
    },
    {
      title: "Web Portal Usage",
      value: "0%",
      target: "60%",
      status: "warning" as const,
      trend: "up" as const,
      trendValue: 0,
      icon: <Globe className="h-3 w-3 ml-1" />
    }
  ];

  // Helper function to get status color classes
  const getStatusColorClasses = (status: "success" | "warning" | "danger" | "neutral") => {
    switch (status) {
      case "success":
        return "bg-green-50 text-green-800 border-green-200";
      case "warning":
        return "bg-amber-50 text-amber-800 border-amber-200";
      case "danger":
        return "bg-red-50 text-red-800 border-red-200";
      case "neutral":
      default:
        return "bg-gray-50 text-gray-800 border-gray-200";
    }
  };

  // Helper function to get trend color classes
  const getTrendColorClass = (trend: "up" | "down" | "neutral") => {
    switch (trend) {
      case "up":
        return "text-green-600";
      case "down":
        return "text-red-600";
      case "neutral":
      default:
        return "text-gray-600";
    }
  };

  // Loading state
  if (isOutstandingLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="card-heading-text flex items-center justify-between">
            Key Metrics Scorecard
            <BarChart2 className="h-5 w-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-muted-foreground">Loading metrics...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (outstandingError) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="card-heading-text flex items-center justify-between">
            Key Metrics Scorecard
            <BarChart2 className="h-5 w-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-red-500">Failed to load metrics</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="card-heading-text flex items-center justify-between">
          Key Metrics Scorecard
          <BarChart2 className="h-5 w-5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <h3 className="card-subheading-text">Financial KPIs</h3>
          <div className="grid grid-cols-2 gap-2">
            {financialMetrics.map((metric) => (
              <Metric key={metric.title} {...metric} />
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="card-subheading-text">Digital Adoption</h3>
          <div className="grid grid-cols-2 gap-2">
            {digitalAdoptionMetrics.map((metric) => (
              <div key={metric.title} className={`p-2 rounded border `}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex justify-between items-center">
                        <span className="card-subheading-text flex items-center">
                          {metric.title}
                          {metric.icon}
                        </span>
                        <CircleDashed className="h-3 w-3 cursor-help" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Target: {metric.target}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="card-value-text">{metric.value}</span>
                  <span className={`text-xs flex items-center ${getTrendColorClass(metric.trend)}`}>
                    {metric.trend === "up" ? <TrendingUp className="h-3 w-3 mr-0.5" /> : 
                    metric.trend === "down" ? <TrendingDown className="h-3 w-3 mr-0.5" /> : null}
                    {metric.trendValue}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
       
      </CardContent>
    </Card>
  );
}